import type { AutomergeUrl } from "@automerge/automerge-repo";
import { useDocument } from "@automerge/react";
import { MessageSquareText } from "lucide-react";
import {
  annotationsOf,
  type AnnotatedDoc,
} from "playground/annotations/annotations";
import { useEffect } from "react";

import { CommentsList } from "@/comments/CommentsList";
import { useTextComments } from "@/comments/text-comments";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** The file's comments toggle for its header, with its open thread count. */
export function CommentsButton({
  url,
  open,
  onToggle,
}: {
  url: AutomergeUrl;
  open: boolean;
  onToggle(): void;
}) {
  const [doc] = useDocument<AnnotatedDoc>(url);
  const count = doc
    ? annotationsOf(doc).filter((annotation) => !annotation.resolved).length
    : 0;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      title="Comments"
      aria-label="Comments"
      aria-pressed={open}
      className={cn("h-8 gap-1 px-2", open && "bg-muted")}
      onClick={onToggle}
    >
      <MessageSquareText className="h-4 w-4" />
      {count > 0 ? <span className="text-xs tabular-nums">{count}</span> : null}
    </Button>
  );
}

/**
 * Every thread on the file, over its content. Picking one hands it to the
 * pane to reveal in place; a thread whose text is gone opens here instead.
 */
export function FileComments({
  url,
  path,
  focus,
  onReveal,
  onClose,
}: {
  url: AutomergeUrl;
  path: string;
  /** A thread to open here, one whose text is gone so it can't open in place. */
  focus?: { threadId: string };
  onReveal(threadId: string): void;
  onClose(): void;
}) {
  const comments = useTextComments(url, path);
  const { activate } = comments;
  const loaded = comments.source !== undefined;
  useEffect(() => {
    if (focus && loaded) activate(focus.threadId);
    // `activate` is a fresh closure each render; the focus is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, loaded]);
  return (
    <CommentsList
      threads={comments.threads}
      activeId={comments.activeId}
      unanchored={comments.unanchored}
      onSelect={(thread) =>
        thread.range
          ? onReveal(thread.annotation.id)
          : comments.activate(thread.annotation.id)
      }
      onClose={onClose}
    />
  );
}
